if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        // ADA #18 - "Add to cart" carries no aria-haspopup, with or without the theme's cart drawer.

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true' || this.submitButton.disabled) return;

        this.handleErrorMessage();

        // Use disabled attribute to prevent multiple clicks and aria-busy for screen readers
        // aria-disabled should only be used when button is truly unavailable (sold out, etc.)
        this.submitButton.disabled = true;
        this.submitButton.setAttribute('aria-busy', 'true');
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error) {
              this.announceCartAddition(response, formData);
              
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            }
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            // Always remove aria-busy since processing is complete
            this.submitButton.removeAttribute('aria-busy');
            if (!this.error) {
              // Re-enable button if no error occurred
              this.submitButton.disabled = false;
            }
            // If error occurred (e.g., sold out), button remains disabled with aria-disabled="true"
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false, forceShow = false) {
        // Buy Now can use this as a fallback before recipient-form is ready.
        if (this.hideErrors && !forceShow) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
          this.errorMessageWrapper.setAttribute('role', 'alert');
          this.errorMessageWrapper.setAttribute('aria-live', 'polite');
          this.errorMessageWrapper.setAttribute('aria-atomic', 'true');
        }
      }

      toggleSubmitButton(disable = true, text, quiet = false) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          // `quiet` is used for transient disables (e.g. while a variant update is in
          // flight) where the button shouldn't look sold out/unavailable, so
          // aria-disabled (which drives the disabled visual styling) is left off.
          if (!quiet) this.submitButton.setAttribute('aria-disabled', 'true');
          if (text) this.submitButtonText.textContent = text;
        } else {
          // The button is rendered with both attributes when the first variant is
          // unavailable, and onSubmitHandler rejects either one, so clear both.
          this.submitButton.removeAttribute('disabled');
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      announceCartAddition(response, formData) {
        const quantity = parseInt(formData.get('quantity')) || 1;
        let productName = '';
        
        const productTitleElement = document.querySelector('h1.product__title');
        if (productTitleElement) {
          productName = productTitleElement.textContent.trim();
        } else {
          const productTitle = this.closest('[data-product-handle]')?.dataset?.productHandle;
          if (productTitle) {
            productName = productTitle.replace(/-/g, ' ');
          }
        }
        
        if (response.items && response.items.length > 0) {
          const addedItem = response.items[response.items.length - 1];
          if (addedItem.product_title) {
            productName = addedItem.product_title;
          }
        }
        
        let announcementText = productName 
          ? `${productName}${quantity > 1 ? ` (${quantity})` : ''} added to cart`
          : `Item${quantity > 1 ? ` (${quantity})` : ''} added to cart`;
        
        if (response.total_price) {
          const cartTotal = this.formatMoney(response.total_price);
          announcementText += `. Cart total: ${cartTotal}`;
        }
        
        const cartStatus = document.getElementById('cart-live-region-text') || 
                          document.getElementById('CartDrawer-LiveRegionText');
        
        if (cartStatus) {
          // Don't use aria-hidden on live regions - let aria-live handle announcements
          cartStatus.textContent = announcementText;

          setTimeout(() => {
            cartStatus.textContent = '';
          }, 2000);
        }
      }

      formatMoney(cents) {
        if (typeof window.Shopify !== 'undefined' && window.Shopify.formatMoney) {
          return window.Shopify.formatMoney(cents);
        }
        const dollars = (cents / 100).toFixed(2);
        return `$${dollars}`;
      }
    }
  );
}
